import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import { darkColors, lightColors } from './colors';

type ColorMode = 'dark' | 'light';

export type AppColors = typeof darkColors;

interface ColorModeContextType {
  mode: ColorMode;
  toggleColorMode: () => void;
  colors: AppColors;
}

const ColorModeContext = createContext<ColorModeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
  colors: lightColors,
});

export const useColorMode = () => useContext(ColorModeContext);
export const useColors = (): AppColors => useContext(ColorModeContext).colors;

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ColorMode>(() => {
    const saved = localStorage.getItem('colorMode');
    return saved === 'dark' ? 'dark' : 'light';
  });

  const toggleColorMode = () => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('colorMode', next);
      return next;
    });
  };

  const colors = mode === 'dark' ? darkColors : lightColors;
  const theme = useMemo(() => createAppTheme(mode, colors), [mode, colors]);

  return (
    <ColorModeContext.Provider value={{ mode, toggleColorMode, colors }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
